// pages/formularios/ListaFormularios.js — aba "Ver e editar formulários".
//
// Out/26. Cartões do design aprovado: nome e status, categoria e tamanho,
// o roteiro das telas ("Seus dados → Como podemos ajudar") e a data que
// importa pra cada status. As ações seguem o que a Meta permite: rascunho
// edita e exclui; publicado duplica e descontinua; descontinuado só duplica.
// PR 2: quem já foi publicado mostra enviados/respondidos e abre a aba
// Respostas daquele formulário. PR 3: "Criar template" abre o construtor de
// templates com o botão deste formulário já escolhido.

import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { FiCopy, FiEdit2, FiFileText, FiInbox, FiRefreshCw, FiSlash, FiTrash2 } from 'react-icons/fi';
import { NOME_CATEGORIA, STATUS } from './regras';
import { SYNE, ajuda, botaoPrincipal, botaoSecundario, botaoTexto, campo, menuPapel, selo } from './estilo';

const FUSO = 'America/Sao_Paulo';
const dia = (d) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
const hora = (d) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(d);

export function quando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(Date.now() - 864e5);
  if (dia(d) === dia(hoje)) return `hoje, ${hora(d)}`;
  if (dia(d) === dia(ontem)) return `ontem, ${hora(d)}`;
  return dia(d);
}

function linhaDeData(f) {
  if (f.status === 'DRAFT') return `Editado ${quando(f.updated_at)}`;
  if (f.status === 'DEPRECATED') return `Descontinuado em ${f.deprecated_at ? dia(new Date(f.deprecated_at)) : '—'}`;
  return `Publicado em ${f.published_at ? dia(new Date(f.published_at)) : '—'}`;
}

const AVISO = {
  THROTTLED: 'A Meta limitou este formulário a 10 envios por hora. Ele volta ao normal sozinho.',
  BLOCKED: 'A Meta bloqueou o envio deste formulário. Duplique e publique a cópia.',
};

const FILTROS = [
  { v: 'ALL', l: 'Todos' }, { v: 'DRAFT', l: 'Rascunhos' }, { v: 'PUBLISHED', l: 'Publicados' },
  { v: 'THROTTLED', l: 'Limitados' }, { v: 'BLOCKED', l: 'Bloqueados' }, { v: 'DEPRECATED', l: 'Descontinuados' },
];

export default function ListaFormularios({
  itens, chips, carregando, atualizando, filtroChip, filtroStatus, onFiltroChip, onFiltroStatus,
  onAtualizar, onEditar, onDuplicar, onExcluir, onDescontinuar, onCriar, onRespostas, onCriarTemplate,
}) {
  const visiveis = itens.filter((f) => (!filtroChip || f.device_id === filtroChip) && (filtroStatus === 'ALL' || f.status === filtroStatus));
  const variosChips = chips.length > 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <TextField
          select size="small" label="Chip (WABA)" value={filtroChip} onChange={(e) => onFiltroChip(e.target.value)}
          SelectProps={{ MenuProps: menuPapel, displayEmpty: true }} InputLabelProps={{ shrink: true }}
          sx={{ ...campo, minWidth: 240, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        >
          <MenuItem value="">Todos os chips</MenuItem>
          {chips.map((c) => <MenuItem key={c.id} value={c.id}>{c.name} — {c.phone_number}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Status" value={filtroStatus} onChange={(e) => onFiltroStatus(e.target.value)}
          SelectProps={{ MenuProps: menuPapel }} sx={{ ...campo, minWidth: 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        >
          {FILTROS.map((f) => <MenuItem key={f.v} value={f.v}>{f.l}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onAtualizar} disabled={atualizando}
          startIcon={atualizando ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <FiRefreshCw size={15} />}
          sx={botaoTexto}
        >
          Atualizar
        </Button>
      </Box>

      {carregando ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={26} sx={{ color: 'var(--fm-green)' }} /></Box>
      ) : !itens.length ? (
        <Box sx={{ border: '1px dashed var(--fm-bdr-h)', borderRadius: '14px', py: 6, px: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--fm-title)', fontSize: '1.05rem' }}>Nenhum formulário ainda</Typography>
          <Typography sx={{ ...ajuda, fontSize: '0.84rem', mt: 0.75, mb: 2 }}>Comece por um modelo da Meta e ajuste as perguntas à sua empresa.</Typography>
          <Button variant="contained" disableElevation onClick={onCriar} sx={{ ...botaoPrincipal, px: 3 }}>Criar formulário</Button>
        </Box>
      ) : !visiveis.length ? (
        <Typography sx={{ ...ajuda, fontSize: '0.86rem', py: 3 }}>Nenhum formulário com estes filtros.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visiveis.map((f) => {
            const st = STATUS[f.status] || STATUS.DRAFT;
            const rascunho = f.status === 'DRAFT';
            const ativo = ['PUBLISHED', 'THROTTLED', 'BLOCKED'].includes(f.status);
            return (
              <Box
                key={f.id} data-formulario={f.id}
                sx={{ border: '1px solid var(--fm-bdr)', borderRadius: '14px', bgcolor: 'var(--fm-paper)', p: { xs: 2, sm: 2.5 }, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: SYNE, fontWeight: 800, fontSize: '1rem', color: 'var(--fm-title)', wordBreak: 'break-word' }}>{f.name}</Typography>
                    <Box component="span" sx={selo(st.tom)}>{st.l}</Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.9, flexWrap: 'wrap' }}>
                    <Box component="span" sx={selo('gray')}>{NOME_CATEGORIA[f.category] || f.category}</Box>
                    <Box component="span" sx={selo('gray')}>{f.telas} tela{f.telas === 1 ? '' : 's'} · {f.campos} campo{f.campos === 1 ? '' : 's'}</Box>
                    {variosChips && f.device_nome && <Box component="span" sx={selo('gray')}>{f.device_nome}</Box>}
                    {!rascunho && (
                      <Box component="span" sx={selo(f.respondidos ? 'green' : 'gray')}>
                        {f.enviados || 0} enviado{f.enviados === 1 ? '' : 's'} · {f.respondidos || 0} resposta{f.respondidos === 1 ? '' : 's'}
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', color: 'var(--fm-label)', mt: 1.1, wordBreak: 'break-word' }}>
                    {(f.roteiro || []).join(' → ')}
                  </Typography>
                  {AVISO[f.status] && (
                    <Typography sx={{ fontSize: '0.8rem', color: f.status === 'BLOCKED' ? 'var(--fm-red)' : 'var(--fm-amber-t)', fontWeight: 600, mt: 0.9 }}>{AVISO[f.status]}</Typography>
                  )}
                  <Typography sx={{ ...ajuda, mt: 1 }}>{linhaDeData(f)}</Typography>
                </Box>
                {/* No celular as ações quebram em duas por linha: com "Criar template"
                    (PR 3) eram quatro botões espremidos numa linha só. */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, alignItems: 'stretch', flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                  {rascunho && (
                    <>
                      <Button variant="outlined" startIcon={<FiEdit2 size={14} />} onClick={() => onEditar(f)} sx={{ ...botaoSecundario, flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap' }}>Editar</Button>
                      <Button
                        variant="outlined" startIcon={<FiTrash2 size={14} />} onClick={() => onExcluir(f)}
                        sx={{ ...botaoSecundario, color: 'var(--fm-red)', borderColor: 'var(--fm-red-b)', flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'var(--fm-red-bg)', borderColor: 'var(--fm-red)' } }}
                      >
                        Excluir
                      </Button>
                    </>
                  )}
                  {!rascunho && (
                    <Button variant="outlined" startIcon={<FiInbox size={14} />} onClick={() => onRespostas?.(f)} sx={{ ...botaoSecundario, flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap' }}>Respostas</Button>
                  )}
                  {['PUBLISHED', 'THROTTLED'].includes(f.status) && onCriarTemplate && (
                    <Button variant="outlined" startIcon={<FiFileText size={14} />} onClick={() => onCriarTemplate(f)} sx={{ ...botaoSecundario, flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap' }}>Criar template</Button>
                  )}
                  {!rascunho && (
                    <Button variant="outlined" startIcon={<FiCopy size={14} />} onClick={() => onDuplicar(f)} sx={{ ...botaoSecundario, flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap' }}>Duplicar</Button>
                  )}
                  {ativo && (
                    <Button startIcon={<FiSlash size={13} />} onClick={() => onDescontinuar(f)} sx={{ ...botaoTexto, color: 'var(--fm-body)', flex: { xs: '1 1 auto', sm: 'none' }, whiteSpace: 'nowrap', '&:hover': { bgcolor: 'var(--fm-red-bg)', color: 'var(--fm-red)' } }}>
                      Descontinuar
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
